import type{
  PersonalSecurityPayload,
  PersonDetail,
  PersonActivity,
  PersonPasskey,
  PersonRenameResult,
  SessionRow,
}from"./types";
const SECURITY="/api/v1/security-center";
const AUTHORITY="/api/v1/security-authority";
const DEVICE="/api/v1/device-authority";

async function req<T>(base:string,path:string,init?:RequestInit):Promise<T>{
  const r=await fetch(`${base}${path}`,{
    credentials:"include",
    headers:{Accept:"application/json","Content-Type":"application/json",...(init?.headers??{})},
    ...init,
  });
  if(!r.ok){
    let detail=r.status===401?"authentication_required":r.status===403?"permission_denied":`request_failed_${r.status}`;
    try{const body=await r.json();if(typeof body?.detail==="string")detail=body.detail}catch{}
    throw new Error(detail);
  }
  return r.json() as Promise<T>;
}

export const securityApi={
  my:()=>req<PersonalSecurityPayload>(SECURITY,"/my"),
  overview:()=>req<unknown>(SECURITY,"/overview"),
  users:()=>req<unknown>(SECURITY,"/users"),
  passkeys:()=>req<{passkeys:PersonPasskey[]}>(SECURITY,"/passkeys"),
  vaults:()=>req<unknown>(SECURITY,"/vaults"),
  access:()=>req<unknown>(SECURITY,"/access"),
  requests:()=>req<unknown>(SECURITY,"/requests"),
  sessions:()=>req<{sessions:SessionRow[];current_session_id:string|null}>(SECURITY,"/sessions"),
  recovery:()=>req<unknown>(SECURITY,"/recovery"),
  audit:()=>req<unknown>(SECURITY,"/audit?limit=100"),
  policies:()=>req<unknown>(SECURITY,"/policies"),
  settings:()=>req<unknown>(SECURITY,"/settings"),
  decideRequest:(requestId:string,decision:"APPROVE"|"DENY")=>
    req<unknown>(SECURITY,`/requests/${encodeURIComponent(requestId)}/decision`,{
      method:"POST",
      body:JSON.stringify({decision}),
    }),
  people:()=>req<{people:unknown[]}>(SECURITY,"/people"),
  personDetail:(subjectId:string)=>req<PersonDetail>(SECURITY,`/people/${encodeURIComponent(subjectId)}`),
  personActivity:(subjectId:string,limit=100)=>req<PersonActivity>(SECURITY,`/people/${encodeURIComponent(subjectId)}/activity?limit=${limit}`),
  attention:()=>req<{findings:unknown[]}>(SECURITY,"/attention"),
  revokeSession:(sessionId:string,ceremonyId:string,credential:Record<string,unknown>,prfResult:string)=>
    req<{session_id:string;revoked:boolean}>(SECURITY,`/sessions/${encodeURIComponent(sessionId)}/revoke/verify`,{
      method:"POST",
      body:JSON.stringify({ceremony_id:ceremonyId,credential,prf_result:prfResult}),
    }),
  renamePasskey:(credentialUuid:string,label:string,reason:string,ceremonyId:string,credential:Record<string,unknown>,prfResult:string)=>
    req<PersonRenameResult>(SECURITY,`/passkeys/${encodeURIComponent(credentialUuid)}/rename/verify`,{
      method:"POST",
      body:JSON.stringify({label,reason,ceremony_id:ceremonyId,credential,prf_result:prfResult}),
    }),
  updatePersonProfile:(subjectId:string,body:Record<string,unknown>)=>
    req<unknown>(SECURITY,`/people/${encodeURIComponent(subjectId)}/profile/verify`,{
      method:"POST",
      body:JSON.stringify(body),
    }),
  upsertPersonAddress:(subjectId:string,body:Record<string,unknown>)=>
    req<unknown>(SECURITY,`/people/${encodeURIComponent(subjectId)}/addresses/verify`,{
      method:"POST",
      body:JSON.stringify(body),
    }),
  deletePersonAddress:(subjectId:string,body:Record<string,unknown>)=>
    req<unknown>(SECURITY,`/people/${encodeURIComponent(subjectId)}/addresses/delete/verify`,{
      method:"POST",
      body:JSON.stringify(body),
    }),
  expireDesktopHandoffs:()=>req<unknown>(SECURITY,"/desktop-handoffs/expire",{method:"POST"}),
};

export const authorityApi={
  status:()=>req<unknown>(AUTHORITY,"/status"),
  unlockOptions:()=>req<unknown>(
    AUTHORITY,
    "/unlock/options",
    {method:"POST"},
  ),
  unlockVerify:(proof:unknown)=>req<unknown>(
    AUTHORITY,
    "/unlock/verify",
    {
      method:"POST",
      body:JSON.stringify(proof),
    },
  ),
};

export const deviceAuthorityApi={
  me:()=>req<unknown>(DEVICE,"/me"),
  state:()=>req<{credentials:Array<Record<string,unknown>>;authority:unknown}>(DEVICE,"/state"),
  loginOptions:()=>req<{ceremony_id:string;publicKey:unknown}>(DEVICE,"/login/options",{method:"POST"}),
  loginVerify:(body:{ceremony_id:string;credential:Record<string,unknown>})=>
    req<unknown>(DEVICE,"/login/verify",{
      method:"POST",
      body:JSON.stringify(body),
    }),
  stepUpOptions:()=>req<{ceremony_id:string;publicKey:unknown}>(DEVICE,"/step-up/options",{method:"POST",body:"{}"}),
  enrollOptions:(label:string)=>req<{ceremony_id:string;currentCredential:unknown;newCredential:unknown}>(
    DEVICE,"/credentials/enroll/options",{
      method:"POST",
      body:JSON.stringify({label}),
    },
  ),
  enrollVerify:(body:{
    ceremony_id:string;
    current_credential:Record<string,unknown>;
    current_prf_result:string;
    new_credential:Record<string,unknown>;
    new_prf_result:string;
    allow_auth_only:boolean;
    label:string;
    trust_group:string;
  })=>req<{verified:boolean;credential_uuid:string;prf_capable:boolean;unlock_capable:boolean;mode:string}>(
    DEVICE,"/credentials/enroll/verify",{
      method:"POST",
      body:JSON.stringify(body),
    },
  ),
  revokeCredential:(credentialUuid:string,proof:{ceremony_id:string;credential:Record<string,unknown>;prf_result:string})=>
    req<{revoked:boolean}>(DEVICE,`/credentials/${encodeURIComponent(credentialUuid)}/revoke/verify`,{
      method:"POST",
      body:JSON.stringify(proof),
    }),
};

const h=()=>window.location.hostname;
export const authorityUiUrl=()=>`${window.location.protocol}//${h()}:8443/security-authority`;
export const legacySecurityUiUrl=()=>`${window.location.protocol}//${h()}:8443/security-center`;
export const vaultConsoleUrl=()=>`${window.location.protocol}//${h()}:8443/security-console`;
